/**
 *
 * PortalAppBar
 *
 * @version 1.0.0
 * @author giuseppe.paternostro
 * @since 22-Feb-2023
 */

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import style from './PortalAppBar.scss';
// import component
import PortalButton from '../PortalButton/PortalButton';
import { getIconsAsset, getBankAssets } from 'Utility/assetProvider';
// import MUI component
import { AppBar, Grid, Typography, IconButton, CircularProgress } from '@mui/material';
// import component mui for header sticky
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import { ReactSVG } from 'react-svg';
import { getObjBankInServicing } from '../../utils/strings';
import { useNavigate } from 'react-router-dom';

function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction='down' in={!trigger}>
      {children}
    </Slide>
  );
}

HideOnScroll.propTypes = {
  children: PropTypes.element.isRequired,
  window: PropTypes.func,
};

const PortalAppBar = (props) => {
  const [logo, setLogo] = useState(null);
  const retryToExtracLogoCounter = useRef();
  retryToExtracLogoCounter.current = 0;
  const navigate = useNavigate();

  const getLogo = (intervalId) => {
    if (retryToExtracLogoCounter.current < 3) {
      // extract logo
      const extractedLogo = getBankAssets(
        props.tenantName,
        props.projectName,
        getObjBankInServicing(props.bank, 'cmsBankInitialization'),
        'logo_dark',
        'svg',
      );

      // try to render the svg
      // if svg is loaded correctly, then I stop the interval and set logo
      // else I increment the counter
      const image = new Image();
      image.onload = () => {
        setLogo(extractedLogo);
        clearInterval(intervalId);
        retryToExtracLogoCounter.current = 3;
      };
      image.onerror = () => {
        retryToExtracLogoCounter.current += 1;
        clearInterval(intervalId);
        const newIntervalId = setInterval(() => {
          getLogo(newIntervalId);
        }, 1000);
      };

      //load the src into image
      image.src = extractedLogo || '';
    } else clearInterval(intervalId);
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      getLogo(intervalId);
    }, 1000);
    return () => {
      clearInterval(intervalId);
      retryToExtracLogoCounter.current = null;
    };
  }, []);

  const useStyles = createUseStyles({
    svgColor: {
      '& *': {
        fill: props.theme['C1-1'],
      },
    },
  });

  const loadingColor = createUseStyles({
    customCircularProgress: {
      color: props.theme['C1-1'],
    },
  });

  const clickOnLogo = () => {
    props.drawerOpen ? props.handleOpenDrawerFunction() : '';
    navigate(props.pathLogoNavigation);
  };

  const loadingLogoColor = loadingColor();
  const classes = useStyles();

  return (
    !props.hasNoHeader &&
    props.enableData && (
      <HideOnScroll {...props}>
        <AppBar
          aria-label='header '
          className={`${style.headerContainer} ${props.drawerOpen ? style.drawerOpen : ''}`}
          position={`${props.drawerOpen ? 'fixed' : 'sticky'}`}>
          {/* section that contain hamburger menu and logo */}
          <Grid className={style.container}>
            <Grid className={style.content}>
              <Grid className={style.menuAndLogoSection}>
                <IconButton
                  title='open and close navigation menu'
                  disableRipple
                  disableFocusRipple
                  onClick={props.handleOpenDrawerFunction}
                  onKeyDown={props.handleKeyDropDown}>
                  <ReactSVG
                    title='burger menu'
                    src={
                      props.drawerOpen
                        ? `${getIconsAsset(props.tenantName, props.projectName, 'svg', 'close')}`
                        : `${getIconsAsset(
                            props.tenantName,
                            props.projectName,
                            'svg',
                            'burgerMenu',
                          )}`
                    }
                    className={`${style.menuIcon} ${classes.svgColor}`}
                    aria-label={
                      props.drawerOpen
                        ? 'close button, to close press esc'
                        : 'burger menu, to open press enter'
                    }
                  />
                </IconButton>
                {!logo ? (
                  <CircularProgress
                    onClick={clickOnLogo}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        clickOnLogo();
                      }
                    }}
                    className={`${style.logo} ${loadingLogoColor.customCircularProgress}`}
                    tabIndex={0}
                    disableShrink
                    alt={props.tenantName}
                  />
                ) : (
                  <div
                    className={style.logo}
                    role='button'
                    tabIndex={0}
                    onClick={clickOnLogo}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        clickOnLogo();
                      }
                    }}>
                    <img src={logo} alt={props.tenantName} />
                  </div>
                )}
              </Grid>
              {props.multiplePosition && (
                <Grid className={style.switchPositionSection}>
                  <Grid className={style.currentPosition}>
                    <Typography variant={'subtitle4'} className={style.positionName}>
                      {props.currentPositionLabel}
                    </Typography>
                    <Typography variant={'subtitle4'} className={style.positionPan}>
                      {props.currentPositionValue}
                    </Typography>
                  </Grid>
                  <Grid>
                    <PortalButton
                      variant={'outlined'}
                      componentColor='secondary'
                      className={style.switchPositionButton}
                      isTranslatedMessage
                      onClick={props.switchPositionFunc}
                      contentText={{
                        id: 't.app.header.switchPosition.button',
                        defaultMessage: 'SWITCH POSITION',
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </AppBar>
      </HideOnScroll>
    )
  );
};

PortalAppBar.propTypes = {
  // Add here some propTypes
  // and set a minimal description i.e.
  /** Description of prop "baz". */
  positionArray: PropTypes.array.isRequired,
  handleOpenDrawerFunction: PropTypes.func.isRequired,
  switchPositionFunc: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  pathLogoNavigation: PropTypes.string,
  hasNoHeader: PropTypes.bool,
  enableData: PropTypes.bool,
  classHeaderContainer: PropTypes.string,
  currentPositionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentPositionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenantName: PropTypes.string,
  projectName: PropTypes.string,
  bank: PropTypes.string,
  multiplePosition: PropTypes.bool,
  handleKeyDropDown: PropTypes.func,
  actions: PropTypes.object,
  theme: PropTypes.object,
};

PortalAppBar.defaultProps = {
  // Add here some default propTypes values
  pathLogoNavigation: '',
  positionArray: [],
  hasNoHeader: false,
  enableData: false,
  drawerOpen: false,
  multiplePosition: true,
  tenantName: '',
  projectName: '',
  actions: {},
  theme: {},
  bank: '',
};

export default PortalAppBar;
